import "../lib/billing_preview_details";
import "../lib/report_fullscreen";
import "../lib/floorplan_report";

// Example: Load Rails libraries in Vite.
//
// import * as Turbo from '@hotwired/turbo'
// Turbo.start()
//
// import ActiveStorage from '@rails/activestorage'
// ActiveStorage.start()
//
// // Import all channels.
// const channels = import.meta.glob('./**/*_channel.js', { eager: true })

// Example: Import a stylesheet in app/frontend/index.css
// import '~/index.css'

function loadContentScreenEditor() {
  if (!document.querySelector("[data-content-screen-editor]")) return;
  import("./content_screen_editor");
}

function loadContentScreenPreview() {
  if (!document.querySelector("[data-content-screen-preview]")) return;
  import("./content_screen_preview").then((module) => module.mountContentScreenPreviews());
}

function loadMemberAppPreview() {
  import("./member_app_preview").then((module) => {
    module.initMemberAppPreview(document);
    module.initMemberAppPreviewModalLauncher(document);
  });
}

function initContentScreenPreviewModal(root = document) {
  const modal = root.querySelector("[data-content-screen-preview-modal]");
  if (!modal || modal.dataset.contentScreenPreviewModalBound === "true") return;

  modal.dataset.contentScreenPreviewModalBound = "true";
  const openButtons = Array.from(document.querySelectorAll("[data-content-screen-preview-modal-open]"));
  const closeButtons = Array.from(modal.querySelectorAll("[data-content-screen-preview-modal-close]"));
  const body = modal.querySelector("[data-content-screen-preview-modal-body]");
  let request = null;
  let previewModule = null;
  let lastTrigger = null;

  function closeModal() {
    request?.abort();
    if (body) {
      previewModule?.unmountContentScreenPreviews(body);
      body.replaceChildren();
    }
    modal.hidden = true;
    document.body.classList.remove("content-screen-preview-modal-open");
    document.removeEventListener("keydown", handleKeydown);
    if (lastTrigger) lastTrigger.focus({ preventScroll: true });
  }

  function handleKeydown(event) {
    if (event.key === "Escape") closeModal();
  }

  async function openModal(event) {
    event.preventDefault();
    lastTrigger = event.currentTarget;
    modal.hidden = false;
    document.body.classList.add("content-screen-preview-modal-open");
    document.addEventListener("keydown", handleKeydown);
    const closeButton = modal.querySelector("[data-content-screen-preview-modal-close]:not(.content-screen-preview-modal-backdrop)");
    if (closeButton) closeButton.focus({ preventScroll: true });
    if (!body) return;
    request?.abort();
    const controller = new AbortController();
    request = controller;
    previewModule?.unmountContentScreenPreviews(body);
    body.textContent = "Loading preview…";
    try {
      const url = new URL(lastTrigger.href, window.location.href);
      url.searchParams.set("panel", "1");
      url.searchParams.set("source", "draft");
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: "text/html" } });
      if (!response.ok || response.redirected) throw new Error("Preview request failed");
      const html = await response.text();
      previewModule = await import("./content_screen_preview");
      if (controller.signal.aborted) return;
      body.innerHTML = html;
      if (!body.querySelector("[data-content-screen-preview]")) throw new Error("Preview missing");
      previewModule.mountContentScreenPreviews(body);
    } catch (error) {
      if (!controller.signal.aborted) body.textContent = "Preview could not load. Close and reopen it to try again.";
    }
  }

  openButtons.forEach((button) => button.addEventListener("click", openModal));
  closeButtons.forEach((button) => button.addEventListener("click", closeModal));
}

function initAgreementReviewPdfModal(root = document) {
  if (!root || typeof root.querySelector !== "function") root = document;

  const modal = root.querySelector("[data-agreement-review-pdf-modal]");
  if (!modal || modal.dataset.agreementReviewPdfModalBound === "true") return;

  modal.dataset.agreementReviewPdfModalBound = "true";
  const openButtons = Array.from(document.querySelectorAll("[data-agreement-review-pdf-modal-open]"));
  const closeButtons = Array.from(modal.querySelectorAll("[data-agreement-review-pdf-modal-close]"));
  const frame = modal.querySelector("[data-agreement-review-pdf-frame]");
  let lastTrigger = null;

  function closeModal() {
    modal.hidden = true;
    document.body.classList.remove("agreement-review-pdf-modal-open");
    document.removeEventListener("keydown", handleKeydown);
    if (lastTrigger) lastTrigger.focus({ preventScroll: true });
  }

  function handleKeydown(event) {
    if (event.key === "Escape") closeModal();
  }

  function openModal(event) {
    event.preventDefault();
    lastTrigger = event.currentTarget;

    if (frame && !frame.hasAttribute("src")) {
      frame.setAttribute("src", frame.dataset.src);
    }

    modal.hidden = false;
    document.body.classList.add("agreement-review-pdf-modal-open");
    document.addEventListener("keydown", handleKeydown);
    const closeButton = modal.querySelector("[data-agreement-review-pdf-modal-close]:not(.content-screen-preview-modal-backdrop)");
    if (closeButton) closeButton.focus({ preventScroll: true });
  }

  openButtons.forEach((button) => button.addEventListener("click", openModal));
  closeButtons.forEach((button) => button.addEventListener("click", closeModal));
}

function loadArticleEditor(root = document) {
  if (!root.querySelector("[data-article-editor]")) return;
  import("./article_editor").then((module) => {
    if (module && module.initArticleEditors) module.initArticleEditors(root);
  });
}

function loadGuideBuilder(root = document) {
  if (!root.querySelector("[data-guide-builder], [data-guide-cover-picker], [data-guide-library-rich-editor]")) return;
  import("./guide_builder").then((module) => {
    if (module && module.initGuideBuilders) module.initGuideBuilders(root);
    if (module && module.initGuideCoverPickers) module.initGuideCoverPickers(root);
    if (module && module.initGuideLibraryRichEditors) module.initGuideLibraryRichEditors(root);
  });
}

function loadMailoutEditor(root = document) {
  if (!root.querySelector("[data-mailout-editor]")) return;
  import("./mailout_editor").then((module) => {
    if (module && module.initMailoutEditors) module.initMailoutEditors(root);
  });
}

function loadNotificationTemplateEditor(root = document) {
  if (!root || typeof root.querySelector !== "function") root = document;
  if (!root.querySelector("[data-template-editor]")) return;
  import("../components/templates/TemplateEditorApp").then((module) => {
    if (module && module.initTemplateEditors) module.initTemplateEditors(root);
  });
}

function loadTicketComposerEditor() {
  if (!document.querySelector("[data-ticket-rich-editor]")) return;
  import("./ticket_composer_editor");
}

function loadTicketSubjectEditor() {
  if (!document.querySelector("[data-ticket-subject-editor]")) return;
  import("./ticket_subject_editor");
}

function loadSimpleWysiwyg() {
  if (!document.querySelector("[data-simple-wysiwyg]")) return;
  import("./simple_wysiwyg").then((module) => {
    if (module && module.initSimpleWysiwygEditors) module.initSimpleWysiwygEditors(document);
  });
}

function loadTicketWhiteboard() {
  if (!document.querySelector("[data-ticket-whiteboard]")) return;
  import("./ticket_whiteboard").then((module) => {
    if (module && module.initTicketWhiteboards) module.initTicketWhiteboards(document);
  });
}

function loadTicketAiTools() {
  if (!document.querySelector("[data-ticket-ai-tools]")) return;
  import("./ticket_ai_tools").then((module) => {
    if (module && module.initTicketAiTools) module.initTicketAiTools(document);
  });
}

function loadTicketThreads() {
  if (!document.querySelector("[data-ticket-new-thread]")) return;
  import("./ticket_threads").then((module) => {
    if (module && module.initTicketThreadMenus) module.initTicketThreadMenus(document);
  });
}

function scrollTicketShowToBottom() {
  if (!document.querySelector("[data-ticket-show-scroll-bottom]")) return;
  if (window.location.hash) return;

  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  const scrollBottom = () => {
    const bottom = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight
    );
    window.scrollTo({ top: bottom, behavior: "auto" });
  };

  window.requestAnimationFrame(() => {
    scrollBottom();
    [50, 150, 350, 700].forEach((delay) => window.setTimeout(scrollBottom, delay));
  });
}

function loadBookingScheduler() {
  if (!document.querySelector("[data-booking-scheduler]")) return;
  import("./booking_scheduler");
}

function loadAttendanceTimeline() {
  if (!document.querySelector("[data-attendance-timeline]")) return;
  import("./attendance_timeline");
}

function loadAgreementOverview() {
  if (!document.querySelector("[data-agreement-overview]")) return;
  import("./agreement_overview");
}

function loadIntelligenceBuilder() {
  if (!document.querySelector("[data-intelligence-builder]")) return;
  import("./intelligence_builder").then((module) => {
    if (module && module.initIntelligenceBuilders) module.initIntelligenceBuilders(document);
  });
}

function loadDashboardLayout() {
  if (!document.querySelector("[data-dashboard-layout]")) return;
  import("./dashboard_layout").then((module) => {
    if (module && module.initDashboardLayouts) module.initDashboardLayouts(document);
  });
}

function loadLocalOfferEditor(root = document) {
  if (!root.querySelector("[data-local-offer-editor]")) return;
  import("./local_offer_editor").then((module) => {
    if (module && module.initLocalOfferEditors) module.initLocalOfferEditors(root);
  });
}

function initLocalOfferTermsModal(root = document) {
  const modal = root.querySelector("[data-local-offer-terms-modal]");
  if (!modal || modal.dataset.localOfferTermsModalBound === "true") return;

  modal.dataset.localOfferTermsModalBound = "true";
  const openButtons = Array.from(document.querySelectorAll("[data-local-offer-terms-open]"));
  const closeButtons = Array.from(modal.querySelectorAll("[data-local-offer-terms-close]"));
  let lastTrigger = null;

  function closeModal() {
    modal.hidden = true;
    document.removeEventListener("keydown", handleKeydown);
    if (lastTrigger) lastTrigger.focus();
  }

  function handleKeydown(event) {
    if (event.key === "Escape") closeModal();
  }

  function openModal(event) {
    event.preventDefault();
    event.stopPropagation();
    lastTrigger = event.currentTarget;
    modal.hidden = false;
    document.addEventListener("keydown", handleKeydown);
    const closeButton = modal.querySelector("[data-local-offer-terms-close]:not(.local-offer-terms-modal-backdrop)");
    if (closeButton) closeButton.focus({ preventScroll: true });
  }

  openButtons.forEach((button) => button.addEventListener("click", openModal));
  closeButtons.forEach((button) => button.addEventListener("click", closeModal));
}

function initXeroSetupModal(root = document) {
  root.querySelectorAll("[data-xero-setup-modal]").forEach((modal) => {
    if (!modal || modal.dataset.xeroSetupModalBound === "true") return;

    modal.dataset.xeroSetupModalBound = "true";
    const openButtons = Array.from(root.querySelectorAll("[data-xero-setup-open]")).filter((button) => (button.dataset.xeroSetupOpen || "") === (modal.id || ""));
    const closeButtons = Array.from(modal.querySelectorAll("[data-xero-setup-close]"));
    let lastTrigger = null;

    function closeModal() {
      modal.hidden = true;
      document.removeEventListener("keydown", handleKeydown);
      if (lastTrigger) lastTrigger.focus({ preventScroll: true });
    }

    function handleKeydown(event) {
      if (event.key === "Escape") closeModal();
    }

    function openModal(event) {
      event.preventDefault();
      lastTrigger = event.currentTarget;
      modal.hidden = false;
      document.addEventListener("keydown", handleKeydown);
      modal.querySelector("[data-xero-setup-close]:not(.xero-setup-modal-backdrop)")?.focus({ preventScroll: true });
    }

    openButtons.forEach((button) => button.addEventListener("click", openModal));
    closeButtons.forEach((button) => button.addEventListener("click", closeModal));
  });
}

function initClipboardButtons(root = document) {
  root.querySelectorAll("[data-copy-text]").forEach((button) => {
    if (button.dataset.copyButtonBound === "true") return;
    button.dataset.copyButtonBound = "true";
    const originalText = button.textContent;

    button.addEventListener("click", async () => {
      const value = button.dataset.copyText || "";
      if (!value) return;

      try {
        await navigator.clipboard.writeText(value);
        button.classList.add("is-copied");
        button.querySelector("span") ? button.querySelector("span").textContent = "Copied" : button.textContent = "Copied";
        window.setTimeout(() => {
          button.classList.remove("is-copied");
          if (button.querySelector("span")) button.querySelector("span").textContent = "Copy";
          else button.textContent = originalText;
        }, 1800);
      } catch {
        window.prompt("Copy link", value);
      }
    });
  });
}

function initCopyLinkButtons(root = document) {
  root.querySelectorAll("[data-copy-link-url]").forEach((button) => {
    if (button.dataset.copyLinkBound === "true") return;
    button.dataset.copyLinkBound = "true";
    const label = button.querySelector("span");
    const originalText = label ? label.textContent : button.textContent;

    button.addEventListener("click", async () => {
      const url = button.dataset.copyLinkUrl || "";
      if (!url) return;

      button.disabled = true;
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
          },
          credentials: "same-origin"
        });
        if (!response.ok) throw new Error("Unable to create link");

        const payload = await response.json();
        const value = payload.access_url || "";
        if (!value) throw new Error("Missing link");

        try {
          await navigator.clipboard.writeText(value);
          button.classList.add("is-copied");
          if (label) label.textContent = "Copied";
          else button.textContent = "Copied";
          button.closest("details")?.removeAttribute("open");
          window.setTimeout(() => {
            button.classList.remove("is-copied");
            if (label) label.textContent = originalText;
            else button.textContent = originalText;
          }, 1800);
        } catch {
          window.prompt("Copy contributor link", value);
        }
      } catch {
        window.alert("Could not create the contributor link. Please try again.");
      } finally {
        button.disabled = false;
      }
    });
  });
}

const unsavedFormBaselines = new WeakMap();

function canonicalUnsavedValue(value) {
  if (value instanceof File) return `file:${value.name}:${value.size}:${value.lastModified}`;
  const stringValue = String(value);
  const trimmed = stringValue.trim();
  if (!trimmed || (!trimmed.startsWith("{") && !trimmed.startsWith("["))) return stringValue;

  try {
    return `json:${JSON.stringify(canonicalUnsavedJson(JSON.parse(trimmed)))}`;
  } catch (_error) {
    return stringValue;
  }
}

function canonicalUnsavedJson(value) {
  if (Array.isArray(value)) return value.map(canonicalUnsavedJson);
  if (!value || typeof value !== "object") return value;

  return Object.keys(value).sort().reduce((result, key) => {
    result[key] = canonicalUnsavedJson(value[key]);
    return result;
  }, {});
}

function serializeUnsavedForm(form) {
  const entries = [];
  new FormData(form).forEach((value, key) => entries.push([key, canonicalUnsavedValue(value)]));
  entries.sort(([leftKey, leftValue], [rightKey, rightValue]) => leftKey.localeCompare(rightKey) || leftValue.localeCompare(rightValue));
  return JSON.stringify(entries);
}

function initArticleUnsavedWarnings(root = document) {
  root.querySelectorAll("[data-article-unsaved-warning], [data-guide-unsaved-warning]").forEach((form) => {
    if (form.dataset.articleUnsavedWarningBound === "true") return;
    form.dataset.articleUnsavedWarningBound = "true";
    form.dataset.articleDirty = "false";
    unsavedFormBaselines.set(form, serializeUnsavedForm(form));

    const markDirty = () => {
      form.dataset.articleDirty = serializeUnsavedForm(form) === unsavedFormBaselines.get(form) ? "false" : "true";
    };

    form.addEventListener("input", markDirty);
    form.addEventListener("change", markDirty);
    form.addEventListener("submit", () => {
      form.dataset.articleDirty = "false";
      unsavedFormBaselines.set(form, serializeUnsavedForm(form));
    });
    form.addEventListener("reset", () => window.setTimeout(markDirty));
  });
}

function initArticleTabs(root = document) {
  root.querySelectorAll("[data-article-tabs]").forEach((container) => {
    if (container.dataset.articleTabsBound === "true") return;
    container.dataset.articleTabsBound = "true";

    const buttons = Array.from(container.querySelectorAll("[data-article-tab]"));
    const panels = Array.from(container.querySelectorAll("[data-article-tab-panel]"));
    const activate = (tab) => {
      buttons.forEach((button) => {
        const active = button.dataset.articleTab === tab;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-selected", active ? "true" : "false");
        button.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.articleTabPanel !== tab;
      });
    };

    buttons.forEach((button) => {
      button.addEventListener("click", () => activate(button.dataset.articleTab));
      button.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
        event.preventDefault();
        const currentIndex = buttons.indexOf(button);
        const offset = event.key === "ArrowRight" ? 1 : -1;
        const nextButton = buttons[(currentIndex + offset + buttons.length) % buttons.length];
        nextButton.focus();
        activate(nextButton.dataset.articleTab);
      });
    });

    activate(container.dataset.initialTab || buttons[0]?.dataset.articleTab || "details");
  });
}

function initAdminFlashLayer(root = document) {
  root.querySelectorAll(".admin-flash-layer .admin-flash").forEach((flash) => {
    if (flash.dataset.adminFlashBound === "true") return;
    flash.dataset.adminFlashBound = "true";

    const dismiss = () => {
      if (!flash.isConnected || flash.dataset.adminFlashDismissed === "true") return;
      flash.dataset.adminFlashDismissed = "true";
      flash.classList.add("is-dismissing");
      window.setTimeout(() => {
        const layer = flash.closest(".admin-flash-layer");
        flash.remove();
        if (layer && !layer.querySelector(".admin-flash")) layer.remove();
      }, 180);
    };

    flash.querySelector("[data-admin-flash-close]")?.addEventListener("click", dismiss);
    window.setTimeout(dismiss, 2000);
  });
}

let pendingAdminPublishForm = null;
let pendingAdminPublishSubmitter = null;
let adminPublishLastFocus = null;
let adminPublishSubmitListenerBound = false;

function closeAdminPublishConfirmation() {
  const modal = document.querySelector("[data-admin-publish-modal]");
  if (modal) modal.hidden = true;
  document.body.classList.remove("admin-publish-modal-open");
  pendingAdminPublishForm = null;
  pendingAdminPublishSubmitter = null;
  if (adminPublishLastFocus?.isConnected) adminPublishLastFocus.focus({ preventScroll: true });
}

function openAdminPublishConfirmation(form, submitter, source) {
  const modal = document.querySelector("[data-admin-publish-modal]");
  if (!modal) return;
  const title = modal.querySelector("[data-admin-publish-title]");
  const body = modal.querySelector("[data-admin-publish-body]");
  const details = modal.querySelector("[data-admin-publish-details]");
  const saveNotice = modal.querySelector("[data-admin-publish-save-notice]");
  const confirmButton = modal.querySelector("[data-admin-publish-confirm]");
  const confirmLabel = modal.querySelector("[data-admin-publish-confirm-label]");

  pendingAdminPublishForm = form;
  pendingAdminPublishSubmitter = submitter;
  adminPublishLastFocus = submitter || document.activeElement;
  title.textContent = source.dataset.publishConfirmTitle || "Publish?";
  body.textContent = source.dataset.publishConfirmBody || "Review this item before publishing.";
  confirmLabel.textContent = source.dataset.publishLabel || "Publish";

  let detailItems = [];
  try {
    detailItems = JSON.parse(source.dataset.publishConfirmDetails || "[]");
  } catch (_error) {
    detailItems = [];
  }
  const visibleDetailItems = detailItems.filter(Boolean);
  details.replaceChildren(...visibleDetailItems.map((item) => {
    const element = document.createElement("li");
    element.textContent = item;
    return element;
  }));
  details.hidden = visibleDetailItems.length === 0;
  if (saveNotice) saveNotice.hidden = source.dataset.publishSavesForm !== "true";

  modal.hidden = false;
  document.body.classList.add("admin-publish-modal-open");
  confirmButton.focus({ preventScroll: true });
}

function initAdminPublishConfirmation() {
  const modal = document.querySelector("[data-admin-publish-modal]");
  if (!modal) return;

  // Turbo replaces the modal on navigation but keeps document listeners alive.
  // Bind one global submit listener that always opens the current page's modal.
  if (!adminPublishSubmitListenerBound) {
    adminPublishSubmitListenerBound = true;
    document.addEventListener("submit", (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      if (form.dataset.adminPublishConfirmed === "true") {
        delete form.dataset.adminPublishConfirmed;
        return;
      }

      const submitter = event.submitter;
      const source = submitter?.matches?.("[data-admin-publish-action]") ? submitter : form.matches("[data-admin-publish-action]") ? form : null;
      if (!source) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      openAdminPublishConfirmation(form, submitter, source);
    }, true);
  }

  if (modal.dataset.adminPublishBound === "true") return;
  modal.dataset.adminPublishBound = "true";
  modal.querySelectorAll("[data-admin-publish-cancel]").forEach((button) => button.addEventListener("click", closeAdminPublishConfirmation));
  modal.querySelector("[data-admin-publish-confirm]")?.addEventListener("click", () => {
    if (!pendingAdminPublishForm) return;
    const form = pendingAdminPublishForm;
    const submitter = pendingAdminPublishSubmitter;

    // Toolbar publish buttons live outside the editor form. Preserve their
    // name/value explicitly so the confirmed request always carries publish=1.
    if (submitter?.name) {
      let publishIntent = form.querySelector("input[data-admin-publish-intent]");
      if (!publishIntent) {
        publishIntent = document.createElement("input");
        publishIntent.type = "hidden";
        publishIntent.dataset.adminPublishIntent = "true";
        form.appendChild(publishIntent);
      }
      publishIntent.name = submitter.name;
      publishIntent.value = submitter.value || "1";
    }

    form.dataset.adminPublishConfirmed = "true";

    if (submitter) {
      submitter.setAttribute("aria-disabled", "true");
      submitter.setAttribute("aria-busy", "true");
      const label = submitter.querySelector("span");
      if (label) label.textContent = "Publishing…";
    }

    modal.hidden = true;
    document.body.classList.remove("admin-publish-modal-open");
    form.requestSubmit();
  });
  modal.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeAdminPublishConfirmation();
  });
}

let pendingAdminActionForm = null;
let pendingAdminActionSubmitter = null;
let adminActionLastFocus = null;
let adminActionSubmitListenerBound = false;
let adminReminderPreviewVersion = 0;
function clearAdminReminderPreview(modal) {
  modal?.querySelector("[data-admin-action-reminder-email]")?.replaceChildren();
}

function closeAdminActionConfirmation() {
  adminReminderPreviewVersion += 1;
  const modal = document.querySelector("[data-admin-action-confirm-modal]");
  if (modal) {
    clearAdminReminderPreview(modal);
    modal.hidden = true;
    modal.classList.remove("is-danger");
  }
  document.body.classList.remove("admin-publish-modal-open");
  const overrideName = pendingAdminActionForm?.dataset.adminConfirmOverrideName;
  if (overrideName) pendingAdminActionForm.querySelector(`input[name="${CSS.escape(overrideName)}"]`)?.remove();
  pendingAdminActionForm = null;
  pendingAdminActionSubmitter = null;
  if (adminActionLastFocus?.isConnected) adminActionLastFocus.focus({ preventScroll: true });
}

function openAdminActionConfirmation(form, submitter) {
  const modal = document.querySelector("[data-admin-action-confirm-modal]");
  if (!modal) return;
  const title = modal.querySelector("[data-admin-action-confirm-title]");
  const body = modal.querySelector("[data-admin-action-confirm-body]");
  const details = modal.querySelector("[data-admin-action-confirm-details]");
  const modalIcon = modal.querySelector("[data-admin-action-confirm-icon]");
  const confirmButton = modal.querySelector("[data-admin-action-confirm-submit]");
  const confirmIcon = modal.querySelector("[data-admin-action-confirm-submit-icon]");
  const confirmLabel = modal.querySelector("[data-admin-action-confirm-label]");
  pendingAdminActionForm = form;
  pendingAdminActionSubmitter = submitter;
  const actionMenu = form.closest("details");
  adminActionLastFocus = actionMenu?.querySelector("summary") || submitter || document.activeElement;
  const tone = form.dataset.adminConfirmTone || "primary";
  const danger = tone === "danger";

  const eyebrow = modal.querySelector("[data-admin-action-confirm-eyebrow]");
  if (eyebrow) {
    modal.dataset.adminConfirmDefaultEyebrow ||= eyebrow.textContent;
    eyebrow.textContent = form.dataset.adminConfirmEyebrow || modal.dataset.adminConfirmDefaultEyebrow;
  }
  title.textContent = form.dataset.adminConfirmTitle || "Continue?";
  body.textContent = form.dataset.adminConfirmBody || "Confirm this account change.";
  confirmLabel.textContent = form.dataset.adminConfirmLabel || "Continue";
  modalIcon.className = form.dataset.adminConfirmIcon || (danger ? "fa-solid fa-triangle-exclamation" : "fa-solid fa-check");
  confirmIcon.className = form.dataset.adminConfirmSubmitIcon || (danger ? "fa-solid fa-user-slash" : "fa-solid fa-user-check");
  confirmButton.classList.toggle("btn-danger", danger);
  confirmButton.classList.toggle("btn-primary", !danger);
  modal.classList.toggle("is-danger", danger);

  let detailItems = [];
  try {
    detailItems = JSON.parse(form.dataset.adminConfirmDetails || "[]");
  } catch (_error) {
    detailItems = [];
  }
  const visibleDetailItems = detailItems.filter(Boolean);
  details.replaceChildren(...visibleDetailItems.map((item) => {
    const element = document.createElement("li");
    element.textContent = item;
    return element;
  }));
  details.hidden = visibleDetailItems.length === 0;

  const reasonField = modal.querySelector("[data-admin-action-confirm-reason-field]");
  const reasonInput = modal.querySelector("[data-admin-action-confirm-reason]");
  const reminder = modal.querySelector("[data-admin-action-reminder]");
  const reminderSelect = modal.querySelector("[data-admin-action-reminder-template]");
  const isReminder = Boolean(form.dataset.adminConfirmPreviewUrl);
  adminReminderPreviewVersion += 1;
  if (reminder) {
    reminder.hidden = !isReminder;
    reminderSelect.replaceChildren(new Option("Select reminder template", ""));
    if (isReminder) {
      JSON.parse(form.dataset.adminConfirmReminderOptions || "[]").forEach(([name, key]) => reminderSelect.add(new Option(name, key)));
      modal.querySelector("[data-admin-action-reminder-preview]").hidden = true;
      clearAdminReminderPreview(modal);
      modal.querySelector("[data-admin-action-reminder-status]").textContent = "Select a template to preview the email.";
    }
  }
  confirmButton.disabled = isReminder;
  modal.classList.toggle("has-email-preview", isReminder);
  const needsReason = Boolean(form.dataset.adminConfirmReasonName);
  reasonField.hidden = !needsReason;
  reasonInput.required = needsReason;
  reasonInput.value = "";
  reasonInput.setCustomValidity("");
  if (actionMenu?.open) {
    const menuList = actionMenu.querySelector(".local-offer-link-menu-list");
    if (menuList?.hidePopover && menuList.matches(":popover-open")) menuList.hidePopover();
    actionMenu.removeAttribute("open");
  }
  modal.hidden = false;
  document.body.classList.add("admin-publish-modal-open");
  (isReminder ? reminderSelect : needsReason ? reasonInput : confirmButton).focus({ preventScroll: true });
}

function initAdminActionConfirmation() {
  const modal = document.querySelector("[data-admin-action-confirm-modal]");
  if (!modal) return;

  if (!adminActionSubmitListenerBound) {
    adminActionSubmitListenerBound = true;
    document.addEventListener("submit", (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.dataset.adminConfirmAction !== "true") return;
      if (form.dataset.adminActionConfirmed === "true") {
        delete form.dataset.adminActionConfirmed;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      openAdminActionConfirmation(form, event.submitter);
    }, true);
  }

  if (modal.dataset.adminActionConfirmBound === "true") return;
  modal.dataset.adminActionConfirmBound = "true";
  modal.querySelector("[data-admin-action-reminder-template]")?.addEventListener("change", async (event) => {
    const form = pendingAdminActionForm;
    if (!form?.dataset.adminConfirmPreviewUrl) return;
    const key = event.target.value;
    const version = ++adminReminderPreviewVersion;
    const status = modal.querySelector("[data-admin-action-reminder-status]");
    const preview = modal.querySelector("[data-admin-action-reminder-preview]");
    const confirm = modal.querySelector("[data-admin-action-confirm-submit]");
    confirm.disabled = true;
    preview.hidden = true;
    status.textContent = key ? "Loading email preview…" : "Select a template to preview the email.";
    if (!key) return;
    try {
      const url = new URL(form.dataset.adminConfirmPreviewUrl, window.location.origin);
      url.searchParams.set("template_key", key);
      const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" });
      const data = await response.json();
      if (version !== adminReminderPreviewVersion || pendingAdminActionForm !== form) return;
      if (!response.ok) throw new Error(data.error || "Could not load the email preview.");
      modal.querySelector("[data-admin-action-reminder-subject]").textContent = data.subject;
      modal.querySelector("[data-admin-action-reminder-recipients]").textContent = data.recipients.join(", ") || "No valid recipients";
      clearAdminReminderPreview(modal);
      preview.hidden = false;
      const frame = document.createElement("iframe");
      frame.title = "Reminder email preview";
      frame.setAttribute("sandbox", "allow-same-origin");
      frame.referrerPolicy = "no-referrer";
      frame.style.cssText = "width:100%;height:280px;border:1px solid #dce1ea;border-radius:8px;background:white";
      frame.srcdoc = data.html;
      modal.querySelector("[data-admin-action-reminder-email]").appendChild(frame);
      status.textContent = data.recipients.length ? "The invoice PDF will be attached." : "Add a valid invoice recipient before sending.";
      confirm.disabled = !data.recipients.length;
    } catch (error) {
      if (version === adminReminderPreviewVersion) status.textContent = error.message || "Could not load the email preview.";
    }
  });
  modal.querySelectorAll("[data-admin-action-confirm-cancel]").forEach((button) => button.addEventListener("click", closeAdminActionConfirmation));
  modal.querySelector("[data-admin-action-confirm-submit]")?.addEventListener("click", () => {
    if (!pendingAdminActionForm) return;
    const form = pendingAdminActionForm;
    const submitter = pendingAdminActionSubmitter;
    if (form.dataset.adminConfirmPreviewUrl) {
      if (modal.querySelector("[data-admin-action-confirm-submit]").disabled) return;
      const key = modal.querySelector("[data-admin-action-reminder-template]").value;
      if (!key) return;
      let input = form.querySelector("input[name='template_key']");
      if (!input) { input = document.createElement("input"); input.type = "hidden"; input.name = "template_key"; form.appendChild(input); }
      input.value = key;
    }
    const reasonName = form.dataset.adminConfirmReasonName;
    if (reasonName) {
      const reasonInput = modal.querySelector("[data-admin-action-confirm-reason]");
      reasonInput.setCustomValidity(reasonInput.value.trim() ? "" : "Enter a reason to continue.");
      if (!reasonInput.reportValidity()) return;
      let input = form.querySelector("[data-admin-confirm-reason-value]");
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.dataset.adminConfirmReasonValue = "true";
        form.appendChild(input);
      }
      input.name = reasonName;
      input.value = reasonInput.value.trim();
    }
    const overrideName = form.dataset.adminConfirmOverrideName;
    if (overrideName) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = overrideName;
      input.value = "1";
      form.appendChild(input);
    }
    form.dataset.adminActionConfirmed = "true";
    modal.hidden = true;
    document.body.classList.remove("admin-publish-modal-open");
    form.requestSubmit(submitter || undefined);
  });
  modal.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeAdminActionConfirmation();
  });
}

let personPortalAccessSubmitListenerBound = false;

function initPersonPortalAccessForms() {
  if (personPortalAccessSubmitListenerBound) return;
  personPortalAccessSubmitListenerBound = true;

  document.addEventListener("submit", async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.personPortalAccessAsync !== "true") return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitter = event.submitter || form.querySelector("button[type='submit'], input[type='submit']");
    const originalLabel = submitter instanceof HTMLInputElement ? submitter.value : submitter?.textContent;
    const busyLabel = form.dataset.personPortalAccessBusyLabel || "Enabling…";
    if (submitter) {
      submitter.disabled = true;
      submitter.setAttribute("aria-busy", "true");
      if (submitter instanceof HTMLInputElement) submitter.value = busyLabel;
      else submitter.textContent = busyLabel;
    }

    try {
      const response = await fetch(form.action, {
        method: form.method,
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: new FormData(form),
        credentials: "same-origin"
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Portal access could not be updated.");

      const rowCheckbox = document.querySelector(`[data-company-row-checkbox][value="${CSS.escape(String(payload.person_id))}"]`);
      const rowStatus = rowCheckbox?.closest("tr")?.querySelector(".people-table-portal-status");
      if (rowStatus) {
        const enabled = payload.portal_access_enabled === true;
        rowStatus.classList.toggle("is-enabled", enabled);
        rowStatus.classList.toggle("is-disabled", !enabled);
        rowStatus.textContent = enabled ? "Enabled" : "No portal access";
      }

      const panel = form.closest("[data-page-drawer-panel]");
      const drawerContainer = panel?.closest("[data-page-drawer]");
      const refreshUrl = form.dataset.personPortalAccessRefreshUrl;
      if (!panel || !drawerContainer || !refreshUrl) return;

      const activeTabTarget = panel.querySelector("[data-admin-tab-target].is-active")?.dataset.adminTabTarget;
      const reloadButton = document.createElement("button");
      reloadButton.type = "button";
      reloadButton.hidden = true;
      reloadButton.dataset.pageDrawerOpen = "true";
      reloadButton.dataset.lazyDrawerUrl = refreshUrl;
      reloadButton.dataset.drawerNotice = payload.message || "Member portal access was updated.";
      reloadButton.setAttribute("aria-controls", panel.id);
      drawerContainer.appendChild(reloadButton);

      const restoreTab = (drawerEvent) => {
        if (drawerEvent.detail?.panel !== panel) return;
        document.removeEventListener("flextech:drawer-loaded", restoreTab);
        reloadButton.remove();
        if (!activeTabTarget) return;
        panel.querySelector(`[data-admin-tab-target="${CSS.escape(activeTabTarget)}"]`)?.click();
      };
      document.addEventListener("flextech:drawer-loaded", restoreTab);
      reloadButton.click();
    } catch (error) {
      window.alert(error.message || "Portal access could not be updated.");
      if (submitter) {
        submitter.disabled = false;
        submitter.removeAttribute("aria-busy");
        if (submitter instanceof HTMLInputElement) submitter.value = originalLabel || "Update portal access";
        else submitter.textContent = originalLabel || "Update portal access";
      }
    }
  }, true);
}

function initEvolutionUnitMappingFilters(root = document) {
  root.querySelectorAll("[data-evolution-unit-mapping-filter]").forEach((container) => {
    if (container.dataset.evolutionUnitMappingFilterBound === "true") return;
    container.dataset.evolutionUnitMappingFilterBound = "true";

    const buttons = Array.from(container.querySelectorAll("[data-evolution-unit-type-filter]"));
    const rows = Array.from(container.querySelectorAll("[data-evolution-unit-type-row]"));
    const emptyRow = container.querySelector("[data-evolution-unit-type-empty]");

    const applyFilter = (unitType) => {
      let visibleCount = 0;
      rows.forEach((row) => {
        const visible = unitType === "all" || row.dataset.evolutionUnitTypeRow === unitType;
        row.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      buttons.forEach((button) => {
        const active = button.dataset.evolutionUnitTypeFilter === unitType;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
      });

      if (emptyRow) emptyRow.hidden = visibleCount > 0;
    };

    buttons.forEach((button) => {
      button.setAttribute("aria-pressed", button.classList.contains("is-active") ? "true" : "false");
      button.addEventListener("click", () => applyFilter(button.dataset.evolutionUnitTypeFilter || "all"));
    });
  });
}

function initIntegrationSettingVisibility(root = document) {
  root.querySelectorAll("[data-integration-settings-form]").forEach((form) => {
    if (form.dataset.integrationSettingVisibilityBound === "true") return;

    const conditionalFields = Array.from(form.querySelectorAll("[data-integration-setting-visible-when-key]"));
    if (!conditionalFields.length) return;

    form.dataset.integrationSettingVisibilityBound = "true";
    const sourceKeys = new Set(conditionalFields.map((field) => field.dataset.integrationSettingVisibleWhenKey));

    const updateFields = () => {
      conditionalFields.forEach((field) => {
        const sourceKey = field.dataset.integrationSettingVisibleWhenKey;
        const expectedValue = field.dataset.integrationSettingVisibleWhenValue;
        const source = form.querySelector(`#integration_settings_json_${sourceKey}`);
        const sourceValue = source?.type === "checkbox" ? (source.checked ? "1" : "0") : source?.value;
        const visible = source && sourceValue === expectedValue;

        field.hidden = !visible;
        field.setAttribute("aria-hidden", visible ? "false" : "true");
      });
    };

    sourceKeys.forEach((sourceKey) => {
      const source = form.querySelector(`#integration_settings_json_${sourceKey}`);
      if (source) source.addEventListener("change", updateFields);
    });

    updateFields();
  });
}

function initElectronicSignatureEnvelopeForms(root = document) {
  root.querySelectorAll("[data-electronic-signature-envelope-form]").forEach((form) => {
    if (form.dataset.electronicSignatureEnvelopeFormBound === "true") return;

    const companySignerSource = form.querySelector("[data-electronic-signature-company-signer-source]");
    const tenantSigner = form.querySelector("[data-electronic-signature-tenant-signer]");
    const tenantSignerType = form.querySelector("[data-electronic-signature-tenant-signer-type]");
    const submitButton = form.querySelector("[data-electronic-signature-send-submit]");
    const submitIdle = form.querySelector("[data-electronic-signature-send-idle]");
    const submitBusy = form.querySelector("[data-electronic-signature-send-busy]");
    if (!companySignerSource || !tenantSigner || !tenantSignerType || !submitButton || !submitIdle || !submitBusy) return;

    form.dataset.electronicSignatureEnvelopeFormBound = "true";

    const setFieldsVisible = (selector, visible) => {
      form.querySelectorAll(selector).forEach((field) => {
        field.hidden = !visible;
        field.setAttribute("aria-hidden", visible ? "false" : "true");
        field.querySelectorAll("input, select, textarea").forEach((control) => {
          control.disabled = !visible;
          control.required = visible;
        });
      });
    };

    const updateFields = () => {
      setFieldsVisible("[data-electronic-signature-company-custom-field]", companySignerSource.value === "");

      const customTenantSignerSelected = tenantSigner.value === "custom";
      tenantSignerType.value = customTenantSignerSelected ? "manual" : "admin";
      setFieldsVisible("[data-electronic-signature-tenant-custom-field]", customTenantSignerSelected);

      document.dispatchEvent(new CustomEvent("flextech:select2-init", { detail: { root: form } }));
    };

    companySignerSource.addEventListener("change", updateFields);
    tenantSigner.addEventListener("change", updateFields);
    if (window.jQuery) {
      window.jQuery(companySignerSource).on("select2:select.electronicSignatureEnvelope select2:clear.electronicSignatureEnvelope", updateFields);
      window.jQuery(tenantSigner).on("select2:select.electronicSignatureEnvelope select2:clear.electronicSignatureEnvelope", updateFields);
    }

    const setSubmitting = (submitting) => {
      form.dataset.submitting = submitting ? "true" : "false";
      submitButton.disabled = submitting;
      submitButton.toggleAttribute("aria-busy", submitting);
      submitIdle.hidden = submitting;
      submitBusy.hidden = !submitting;
    };

    setSubmitting(false);
    form.addEventListener("submit", (event) => {
      if (form.dataset.electronicSignatureSubmissionResumed === "true") {
        delete form.dataset.electronicSignatureSubmissionResumed;
        return;
      }

      if (form.dataset.submitting === "true") {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      setSubmitting(true);
      window.requestAnimationFrame(() => {
        if (!form.isConnected) return;
        form.dataset.electronicSignatureSubmissionResumed = "true";
        form.requestSubmit();
      });
    });
    form.addEventListener("turbo:submit-end", (event) => {
      if (!event.detail?.success && form.isConnected) setSubmitting(false);
    });

    updateFields();
  });
}

window.addEventListener("beforeunload", (event) => {
  const dirtyForm = document.querySelector("[data-article-unsaved-warning][data-article-dirty='true'], [data-guide-unsaved-warning][data-article-dirty='true']");
  if (!dirtyForm) return;

  event.preventDefault();
  event.returnValue = "";
});

document.addEventListener("DOMContentLoaded", loadContentScreenEditor);
document.addEventListener("turbo:load", loadContentScreenEditor);
document.addEventListener("DOMContentLoaded", loadContentScreenPreview);
document.addEventListener("turbo:load", loadContentScreenPreview);
document.addEventListener("DOMContentLoaded", loadMemberAppPreview);
document.addEventListener("turbo:load", loadMemberAppPreview);
document.addEventListener("DOMContentLoaded", () => initContentScreenPreviewModal());
document.addEventListener("turbo:load", () => initContentScreenPreviewModal());
document.addEventListener("DOMContentLoaded", () => initAgreementReviewPdfModal());
document.addEventListener("turbo:load", () => initAgreementReviewPdfModal());
document.addEventListener("turbo:before-cache", () => {
  const modal = document.querySelector("[data-agreement-review-pdf-modal]");
  if (!modal) return;

  modal.hidden = true;
  delete modal.dataset.agreementReviewPdfModalBound;
  modal.querySelector("[data-agreement-review-pdf-frame]")?.removeAttribute("src");
  document.body.classList.remove("agreement-review-pdf-modal-open");
});
document.addEventListener("DOMContentLoaded", () => loadArticleEditor());
document.addEventListener("turbo:load", () => loadArticleEditor());
document.addEventListener("DOMContentLoaded", () => loadGuideBuilder());
document.addEventListener("turbo:load", () => loadGuideBuilder());
document.addEventListener("DOMContentLoaded", () => loadMailoutEditor());
document.addEventListener("turbo:load", () => loadMailoutEditor());
document.addEventListener("DOMContentLoaded", loadNotificationTemplateEditor);
document.addEventListener("turbo:load", loadNotificationTemplateEditor);
document.addEventListener("DOMContentLoaded", loadTicketComposerEditor);
document.addEventListener("turbo:load", loadTicketComposerEditor);
document.addEventListener("DOMContentLoaded", loadTicketSubjectEditor);
document.addEventListener("turbo:load", loadTicketSubjectEditor);
document.addEventListener("DOMContentLoaded", loadSimpleWysiwyg);
document.addEventListener("turbo:load", loadSimpleWysiwyg);
document.addEventListener("DOMContentLoaded", loadTicketWhiteboard);
document.addEventListener("turbo:load", loadTicketWhiteboard);
document.addEventListener("DOMContentLoaded", loadTicketAiTools);
document.addEventListener("turbo:load", loadTicketAiTools);
document.addEventListener("DOMContentLoaded", loadTicketThreads);
document.addEventListener("turbo:load", loadTicketThreads);
document.addEventListener("DOMContentLoaded", scrollTicketShowToBottom);
document.addEventListener("turbo:load", scrollTicketShowToBottom);
document.addEventListener("DOMContentLoaded", loadBookingScheduler);
document.addEventListener("turbo:load", loadBookingScheduler);
document.addEventListener("DOMContentLoaded", loadAttendanceTimeline);
document.addEventListener("turbo:load", loadAttendanceTimeline);
document.addEventListener("DOMContentLoaded", loadAgreementOverview);
document.addEventListener("turbo:load", loadAgreementOverview);
document.addEventListener("DOMContentLoaded", loadIntelligenceBuilder);
document.addEventListener("turbo:load", loadIntelligenceBuilder);
document.addEventListener("DOMContentLoaded", loadDashboardLayout);
document.addEventListener("turbo:load", loadDashboardLayout);
document.addEventListener("DOMContentLoaded", () => loadLocalOfferEditor());
document.addEventListener("turbo:load", () => loadLocalOfferEditor());
document.addEventListener("DOMContentLoaded", () => initLocalOfferTermsModal());
document.addEventListener("turbo:load", () => initLocalOfferTermsModal());
document.addEventListener("DOMContentLoaded", () => initXeroSetupModal());
document.addEventListener("turbo:load", () => initXeroSetupModal());
document.addEventListener("DOMContentLoaded", () => initClipboardButtons());
document.addEventListener("turbo:load", () => initClipboardButtons());
document.addEventListener("DOMContentLoaded", () => initCopyLinkButtons());
document.addEventListener("turbo:load", () => initCopyLinkButtons());
document.addEventListener("DOMContentLoaded", () => initArticleUnsavedWarnings());
document.addEventListener("turbo:load", () => initArticleUnsavedWarnings());
document.addEventListener("turbo:before-cache", () => {
  document.querySelectorAll("[data-article-unsaved-warning], [data-guide-unsaved-warning]").forEach((form) => {
    // Turbo caches a cloned DOM without its event listeners. Remove the
    // binding marker and stale dirty state so a restored editor is rebound
    // from a clean baseline on the next turbo:load.
    form.dataset.articleDirty = "false";
    delete form.dataset.articleUnsavedWarningBound;
  });
});
document.addEventListener("DOMContentLoaded", () => initArticleTabs());
document.addEventListener("turbo:load", () => initArticleTabs());
document.addEventListener("DOMContentLoaded", () => initAdminFlashLayer());
document.addEventListener("turbo:load", () => initAdminFlashLayer());
document.addEventListener("DOMContentLoaded", () => initAdminPublishConfirmation());
document.addEventListener("turbo:load", () => initAdminPublishConfirmation());
document.addEventListener("DOMContentLoaded", () => initAdminActionConfirmation());
document.addEventListener("turbo:load", () => initAdminActionConfirmation());
document.addEventListener("DOMContentLoaded", () => initPersonPortalAccessForms());
document.addEventListener("turbo:load", () => initPersonPortalAccessForms());
document.addEventListener("DOMContentLoaded", () => initEvolutionUnitMappingFilters());
document.addEventListener("turbo:load", () => initEvolutionUnitMappingFilters());
document.addEventListener("DOMContentLoaded", () => initIntegrationSettingVisibility());
document.addEventListener("turbo:load", () => initIntegrationSettingVisibility());
document.addEventListener("DOMContentLoaded", () => initElectronicSignatureEnvelopeForms());
document.addEventListener("turbo:load", () => initElectronicSignatureEnvelopeForms());
document.addEventListener("turbo:before-cache", () => {
  document.querySelectorAll("[data-integration-settings-form]").forEach((form) => {
    delete form.dataset.integrationSettingVisibilityBound;
  });
  document.querySelectorAll("[data-electronic-signature-envelope-form]").forEach((form) => {
    delete form.dataset.electronicSignatureEnvelopeFormBound;
  });
});
document.addEventListener("flextech:drawer-loaded", (event) => {
  const root = event.detail && event.detail.panel ? event.detail.panel : document;
  loadLocalOfferEditor(root);
  initClipboardButtons(root);
  initCopyLinkButtons(root);
  initElectronicSignatureEnvelopeForms(root);
});

function adminMobileCsrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || "";
}

let adminPushDeepLinkBound = false;
let adminKeyboardBound = false;
let adminHapticsBound = false;
let adminLifecycleRecoveryBound = false;
let adminHistoryRecoveryBound = false;
let adminInactiveAt = null;
let adminDeviceSyncStarted = false;
const ADMIN_STALE_WEBVIEW_AFTER_MS = 30 * 60 * 1000;

function adminMobileContentFrame() {
  return document.getElementById("admin-mobile-content");
}

function visitAdminMobileContent(url) {
  const frame = adminMobileContentFrame();
  if (frame && window.Turbo?.visit) {
    window.Turbo.visit(url, { frame: frame.id, action: "advance" });
  } else {
    window.location.assign(url);
  }
}

function syncAdminMobileShell(frame) {
  const state = frame.querySelector("[data-admin-app-navigation-state]");
  const template = frame.querySelector("template[data-admin-app-navigation-template]");
  const navigation = document.getElementById("admin-app-bottom-navigation");
  if (!state || !template || !navigation) return;

  navigation.replaceChildren(template.content.cloneNode(true));
  navigation.hidden = state.dataset.adminAppNavigationVisible !== "true";
  if (state.dataset.authorizationRevision) document.body.dataset.authorizationRevision = state.dataset.authorizationRevision;
  if (state.dataset.currentTenantId) document.body.dataset.currentTenantId = state.dataset.currentTenantId;
}

function initAdminMobileHistoryRecovery() {
  if (adminHistoryRecoveryBound) return;
  adminHistoryRecoveryBound = true;

  window.addEventListener("popstate", () => {
    window.setTimeout(() => {
      const frame = adminMobileContentFrame();
      if (!frame) return;

      const source = frame.getAttribute("src");
      const sourceUrl = source ? new URL(source, window.location.href).href : null;
      if (!sourceUrl || sourceUrl === window.location.href) {
        initAdminMobileFrameContent(frame);
        return;
      }

      if (window.Turbo?.visit) {
        window.Turbo.visit(window.location.href, { frame: frame.id, action: "replace" });
      } else {
        window.location.reload();
      }
    }, 0);
  });
}

function adminMobileHaptic(kind = "light") {
  if (!document.body?.matches("[data-admin-mobile-app]") || !window.Capacitor?.isNativePlatform?.()) return Promise.resolve();
  const haptics = window.Capacitor?.Plugins?.Haptics;
  if (!haptics) return Promise.resolve();

  try {
    const operation = (() => {
      switch (kind) {
      case "medium": return haptics.impact?.({ style: "MEDIUM" });
      case "success": return haptics.notification?.({ type: "SUCCESS" });
      case "warning": return haptics.notification?.({ type: "WARNING" });
      case "error": return haptics.notification?.({ type: "ERROR" });
      default: return haptics.impact?.({ style: "LIGHT" });
      }
    })();

    return Promise.resolve(operation).catch(() => {});
  } catch (_error) {
    return Promise.resolve();
  }
}

function initAdminMobileHaptics(root = document) {
  if (!document.body.matches("[data-admin-mobile-app]") || !window.Capacitor?.isNativePlatform?.()) return;

  root.querySelectorAll(".admin-app-flash:not([data-admin-haptic-delivered])").forEach((flash) => {
    flash.dataset.adminHapticDelivered = "true";
    adminMobileHaptic(flash.classList.contains("is-error") ? "error" : "success");
  });

  if (adminHapticsBound) return;
  adminHapticsBound = true;

  document.addEventListener("click", (event) => {
    const origin = event.target instanceof Element ? event.target : event.target?.parentElement;
    const target = origin?.closest("a, button, input[type='submit']");
    if (!target || target.matches("[data-admin-haptic='none']") || target.disabled) return;
    if (target.matches(".admin-app-primary-button, .admin-app-action, [data-mailroom-scan], input[type='submit'], button[type='submit']")) {
      adminMobileHaptic("medium");
    } else if (target.closest(".admin-app-shell")) {
      adminMobileHaptic("light");
    }
  });

  document.addEventListener("change", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.matches(".admin-app-shell select, .admin-app-shell input[type='checkbox'], .admin-app-shell input[type='radio']")) {
      adminMobileHaptic("light");
    }
  });

  let lastInvalidHapticAt = 0;
  document.addEventListener("invalid", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest(".admin-app-shell")) return;
    const now = Date.now();
    if (now - lastInvalidHapticAt < 500) return;
    lastInvalidHapticAt = now;
    adminMobileHaptic("error");
  }, true);
}

async function adminMobileInstallationUuid() {
  const plugins = window.Capacitor?.Plugins;
  if (!plugins?.Preferences) return null;
  const key = "flextech_admin_installation_uuid";
  const existing = await plugins.Preferences.get({ key });
  if (existing.value) return existing.value;
  const value = window.crypto.randomUUID();
  await plugins.Preferences.set({ key, value });
  return value;
}

async function registerAdminMobileDevice({ pushToken = null, permission = null } = {}) {
  if (!document.body.matches("[data-admin-mobile-app]") || !window.Capacitor?.isNativePlatform?.()) return null;
  const installationUuid = await adminMobileInstallationUuid();
  if (!installationUuid) return null;
  const platform = window.Capacitor.getPlatform();
  const appInfoPromise = window.Capacitor.Plugins?.App?.getInfo ? window.Capacitor.Plugins.App.getInfo().catch(() => null) : Promise.resolve(null);
  const deviceInfoPromise = window.Capacitor.Plugins?.Device?.getInfo ? window.Capacitor.Plugins.Device.getInfo().catch(() => null) : Promise.resolve(null);
  const [appInfo, deviceInfo] = await Promise.all([
    appInfoPromise,
    deviceInfoPromise
  ]);
  const devicePayload = {
    installation_uuid: installationUuid,
    platform,
    app_version: appInfo ? `${appInfo.version} (${appInfo.build})` : null,
    os_version: deviceInfo?.osVersion || null
  };
  if (pushToken) devicePayload.push_token = pushToken;
  if (permission) devicePayload.push_permission_state = permission;
  const response = await fetch("/admin/app/devices", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": adminMobileCsrfToken(), "Accept": "application/json" },
    body: JSON.stringify({ device: devicePayload })
  });
  if (!response.ok) throw new Error("Unable to register this device");
  const payload = await response.json();
  window.sessionStorage.setItem("flextech_admin_device_id", payload.device_id);
  return { installationUuid, deviceId: payload.device_id };
}

async function syncAdminMobileDevice() {
  const push = window.Capacitor?.Plugins?.PushNotifications;
  if (!push?.checkPermissions) return registerAdminMobileDevice();
  const result = await push.checkPermissions();
  const permission = result.receive === "granted" ? "granted" : result.receive === "denied" ? "denied" : "not_requested";
  return registerAdminMobileDevice({ permission });
}

async function openAdminBrowserHandoff(button) {
  button.disabled = true;
  try {
    const installationUuid = await adminMobileInstallationUuid();
    const response = await fetch("/admin/app/browser_handoffs", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": adminMobileCsrfToken(), "Accept": "application/json" },
      body: JSON.stringify({ destination_key: button.dataset.destinationKey, installation_uuid: installationUuid })
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || "Unable to open the full admin website");
    const systemBrowser = window.Capacitor?.Plugins?.AdminSystemBrowser;
    if (window.Capacitor?.isNativePlatform?.() && systemBrowser?.open) {
      await systemBrowser.open({ url: payload.url });
    } else if (window.Capacitor?.isNativePlatform?.() && window.Capacitor?.Plugins?.Browser?.open) {
      // Compatibility for installed builds released before the system-browser
      // bridge. Current builds always use AdminSystemBrowser above.
      await window.Capacitor.Plugins.Browser.open({ url: payload.url });
    } else {
      window.open(payload.url, "_blank", "noopener,noreferrer");
    }
  } catch (error) {
    adminMobileHaptic("error");
    window.alert(error.message);
  } finally {
    button.disabled = false;
  }
}

async function enableAdminPush(button) {
  const status = document.querySelector("[data-admin-push-status]");
  const push = window.Capacitor?.Plugins?.PushNotifications;
  if (!push) {
    if (status) status.textContent = "Notifications are available in the installed iOS and Android apps.";
    return;
  }
  button.disabled = true;
  try {
    let permission = await push.checkPermissions();
    if (permission.receive === "prompt" || permission.receive === "prompt-with-rationale") permission = await push.requestPermissions();
    if (permission.receive !== "granted") {
      await registerAdminMobileDevice({ permission: "denied" });
      if (status) status.textContent = "Notifications are disabled. You can enable them in device settings.";
      adminMobileHaptic("warning");
      return;
    }
    await new Promise((resolve, reject) => {
      const registered = push.addListener("registration", async ({ value }) => {
        await registerAdminMobileDevice({ pushToken: value, permission: "granted" });
        registered.remove(); failed.remove(); resolve();
      });
      const failed = push.addListener("registrationError", (error) => {
        registered.remove(); failed.remove(); reject(new Error(error?.error || "Push registration failed"));
      });
      push.register();
    });
    await fetch("/admin/app/preferences", {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": adminMobileCsrfToken(), "Accept": "application/json" },
      body: JSON.stringify({ notification_preferences: { assigned: true, requester_reply: true, urgent: true, overdue: true } })
    });
    if (status) status.textContent = "Helpdesk alerts are enabled on this device.";
    adminMobileHaptic("success");
  } catch (error) {
    if (status) status.textContent = error.message;
    adminMobileHaptic("error");
  } finally {
    button.disabled = false;
  }
}

function initAdminPushDeepLinks() {
  const push = window.Capacitor?.Plugins?.PushNotifications;
  if (!push || adminPushDeepLinkBound) return;

  adminPushDeepLinkBound = true;
  push.addListener("pushNotificationActionPerformed", async ({ notification }) => {
    const ticketId = notification?.data?.ticket_id;
    if (!ticketId || notification?.data?.type !== "service_request") return;
    const tenantId = notification?.data?.tenant_id;
    if (tenantId && tenantId !== document.body.dataset.currentTenantId) {
      const body = new URLSearchParams({ tenant_id: tenantId, return_to: `/admin/tickets/${ticketId}` });
      const response = await fetch("/admin/current_tenant", {
        method: "PATCH",
        headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRF-Token": adminMobileCsrfToken(), "Accept": "text/html" },
        body
      });
      if (!response.ok) return;
    }
    const target = `/admin/tickets/${encodeURIComponent(ticketId)}`;
    if (tenantId && tenantId !== document.body.dataset.currentTenantId) window.location.assign(target);
    else visitAdminMobileContent(target);
  });
}

function initAdminMobileSignout(root = document) {
  root.querySelectorAll("form[data-admin-mobile-signout]").forEach((form) => {
    if (form.dataset.bound) return;
    form.dataset.bound = "true";
    form.addEventListener("submit", async (event) => {
      if (form.dataset.deviceRevoked === "true") return;
      event.preventDefault();
      const installationUuid = await adminMobileInstallationUuid().catch(() => null);
      const installationField = form.querySelector("input[name='admin_mobile_installation_uuid']");
      if (installationField && installationUuid) installationField.value = installationUuid;
      const deviceId = window.sessionStorage.getItem("flextech_admin_device_id");
      if (deviceId) {
        try {
          await fetch(`/admin/app/devices/${encodeURIComponent(deviceId)}`, {
            method: "DELETE",
            headers: { "X-CSRF-Token": adminMobileCsrfToken(), "Accept": "application/json" }
          });
        } catch (_error) {
          // Session logout must remain available even if device cleanup cannot reach the server.
        }
      }
      window.sessionStorage.removeItem("flextech_admin_device_id");
      form.dataset.deviceRevoked = "true";
      form.requestSubmit();
    });
  });
}

function initAdminMobileLifecycleRecovery() {
  if (adminLifecycleRecoveryBound || !window.Capacitor?.isNativePlatform?.()) return;
  const app = window.Capacitor?.Plugins?.App;
  if (!app?.addListener) return;

  adminLifecycleRecoveryBound = true;
  app.addListener("appStateChange", ({ isActive }) => {
    if (!isActive) {
      adminInactiveAt = Date.now();
      return;
    }

    if (!adminInactiveAt) return;
    const inactiveFor = Date.now() - adminInactiveAt;
    adminInactiveAt = null;
    if (inactiveFor < ADMIN_STALE_WEBVIEW_AFTER_MS) return;

    // WKWebView/Android System WebView can retain a visually blank or stale
    // render process after a long suspension. A full document reload also lets
    // Devise restore the durable native session before the next navigation.
    window.location.reload();
  });
}

function initAdminMobileApp(root = document) {
  if (!document.body.matches("[data-admin-mobile-app]")) return;
  initAdminMobileLifecycleRecovery();
  initAdminMobileHistoryRecovery();
  initAdminMobileHaptics(root);
  if (!adminDeviceSyncStarted) {
    adminDeviceSyncStarted = true;
    syncAdminMobileDevice().catch(() => { adminDeviceSyncStarted = false; });
  }
  root.querySelectorAll("[data-admin-browser-handoff]").forEach((button) => {
    if (button.dataset.bound) return;
    button.dataset.bound = "true";
    button.addEventListener("click", () => openAdminBrowserHandoff(button));
  });
  root.querySelectorAll("[data-admin-enable-push]").forEach((button) => {
    if (button.dataset.bound) return;
    button.dataset.bound = "true";
    button.addEventListener("click", () => enableAdminPush(button));
  });
  initAdminPushDeepLinks();
  initAdminMobileSignout(root);
  const keyboard = window.Capacitor?.Plugins?.Keyboard;
  if (keyboard && !adminKeyboardBound) {
    adminKeyboardBound = true;
    keyboard.addListener("keyboardWillShow", () => document.body.classList.add("keyboard-visible"));
    keyboard.addListener("keyboardWillHide", () => document.body.classList.remove("keyboard-visible"));
  }
}

document.addEventListener("DOMContentLoaded", () => initAdminMobileApp());
document.addEventListener("turbo:load", () => initAdminMobileApp());
function setupMailroomScanner(root = document) {
  const button = root.querySelector("[data-mailroom-scan]");
  if (!button) return;
  if (button.dataset.bound === "true") return;
  button.dataset.bound = "true";
  button.addEventListener("click", async () => {
    const resultNode = root.querySelector("[data-mailroom-scan-result]");
    try {
      if (!window.Capacitor?.isNativePlatform?.()) throw new Error("Open FlexTech Admin on your phone to scan a pickup QR.");
      const scanner = window.Capacitor?.Plugins?.BarcodeScanner;
      if (!scanner) throw new Error("The camera scanner is unavailable. Update FlexTech Admin and try again.");
      if (resultNode) resultNode.textContent = "Opening the camera scanner…";
      const permission = await scanner.requestPermissions();
      if (permission?.camera && !["granted", "limited"].includes(permission.camera)) throw new Error("Camera permission is required to scan pickup QR codes.");
      const scan = await scanner.scan();
      const token = scan?.barcodes?.[0]?.rawValue || scan?.barcodes?.[0]?.displayValue;
      if (!token) throw new Error("No Mailroom pickup code was detected.");
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
      const installationUuid = await adminMobileInstallationUuid();
      const response = await fetch("/admin/app/delivery_pickup_scans", { method: "POST", headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf }, body: JSON.stringify({ token, installation_uuid: installationUuid }) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error?.message || "Pickup code could not be verified.");
      adminMobileHaptic("success");
      const data = payload.data;
      const article = document.createElement("article");
      article.className = "mailroom-scan-result";
      const image = document.createElement("img");
      image.src = data.delivery.photo_url;
      image.alt = "Delivery";
      const details = document.createElement("div");
      const heading = document.createElement("h2");
      heading.textContent = data.member.name;
      const company = document.createElement("p");
      company.textContent = data.member.company || "";
      const received = document.createElement("p");
      received.textContent = `${data.delivery.site} · ${new Date(data.delivery.received_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`;
      const state = document.createElement("strong");
      state.textContent = "Waiting for member confirmation";
      details.append(heading, company, received, state);
      article.append(image, details);
      resultNode?.replaceChildren(article);
      const pollUntil = new Date(data.confirmation_expires_at).getTime();
      const poll = async () => {
        if (!article.isConnected || Date.now() >= pollUntil) {
          state.textContent = "Member confirmation expired";
          adminMobileHaptic("warning");
          return;
        }
        try {
          const statusResponse = await fetch(data.status_url, { headers: { "Accept": "application/json" }, cache: "no-store" });
          const statusPayload = await statusResponse.json();
          if (!statusResponse.ok) throw new Error(statusPayload?.error?.message || "Pickup status is unavailable.");
          if (statusPayload.data.status === "confirmed" && statusPayload.data.delivery_status === "collected") {
            state.textContent = "Pickup confirmed by member";
            article.classList.add("is-confirmed");
            adminMobileHaptic("success");
            return;
          }
          if (["expired", "cancelled"].includes(statusPayload.data.status)) {
            state.textContent = statusPayload.data.status === "expired" ? "Member confirmation expired" : "Pickup verification cancelled";
            adminMobileHaptic("warning");
            return;
          }
        } catch (pollError) {
          state.textContent = pollError?.message || "Waiting for member confirmation";
        }
        window.setTimeout(poll, 2000);
      };
      window.setTimeout(poll, 1000);
    } catch (error) {
      if (resultNode) resultNode.textContent = error?.message || "Pickup scan failed.";
      adminMobileHaptic("error");
    }
  });
}

document.addEventListener("DOMContentLoaded", () => setupMailroomScanner());
document.addEventListener("turbo:load", () => setupMailroomScanner());

function setupMailroomDeliveryForm(root = document) {
  const form = root.querySelector("[data-mailroom-delivery-form]");
  if (!form || form.dataset.bound === "true") return;

  const input = form.querySelector("[data-mailroom-photo-input]");
  const picker = form.querySelector(".admin-app-photo-picker");
  const preview = form.querySelector("[data-mailroom-photo-preview]");
  const placeholder = form.querySelector("[data-mailroom-photo-placeholder]");
  const action = form.querySelector("[data-mailroom-photo-action]");
  const name = form.querySelector("[data-mailroom-photo-name]");
  const submit = form.querySelector("[data-mailroom-delivery-submit]");
  const submitIdle = form.querySelector("[data-mailroom-submit-idle]");
  const submitBusy = form.querySelector("[data-mailroom-submit-busy]");
  if (!input || !picker || !preview || !placeholder || !action || !name || !submit || !submitIdle || !submitBusy) return;

  form.dataset.bound = "true";
  let previewUrl = null;
  const setSubmitting = (submitting) => {
    form.dataset.submitting = submitting ? "true" : "false";
    submit.disabled = submitting;
    submit.toggleAttribute("aria-busy", submitting);
    submitIdle.hidden = submitting;
    submitBusy.hidden = !submitting;
  };

  setSubmitting(false);
  form.addEventListener("submit", (event) => {
    if (form.dataset.mailroomSubmissionResumed === "true") {
      delete form.dataset.mailroomSubmissionResumed;
      return;
    }

    if (form.dataset.submitting === "true") {
      event.preventDefault();
      return;
    }

    // Give iOS and Android WebViews a frame to paint the busy state before
    // Turbo begins reading and uploading the delivery photo.
    event.preventDefault();
    setSubmitting(true);
    window.requestAnimationFrame(() => {
      if (!form.isConnected) return;
      form.dataset.mailroomSubmissionResumed = "true";
      form.requestSubmit();
    });
  });
  form.addEventListener("turbo:submit-end", (event) => {
    if (!event.detail?.success && form.isConnected) setSubmitting(false);
  });

  input.addEventListener("change", () => {
    const file = input.files?.[0];
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = null;

    if (!file) {
      preview.removeAttribute("src");
      preview.hidden = true;
      placeholder.hidden = false;
      picker.classList.remove("has-photo");
      action.textContent = "Tap to take or choose a photo";
      name.textContent = "No photo selected";
      return;
    }

    previewUrl = URL.createObjectURL(file);
    preview.src = previewUrl;
    preview.hidden = false;
    placeholder.hidden = true;
    picker.classList.add("has-photo");
    action.textContent = "Tap to retake or choose another photo";
    name.textContent = file.name || "Delivery photo selected";
    adminMobileHaptic("light");
  });
}

document.addEventListener("DOMContentLoaded", () => setupMailroomDeliveryForm());
document.addEventListener("turbo:load", () => setupMailroomDeliveryForm());

function setupAdminAppTicketCreateForm(root = document) {
  const form = root.querySelector("[data-admin-app-ticket-create-form]");
  if (!form || form.dataset.submissionBound === "true") return;

  const submit = form.querySelector("[data-admin-app-ticket-submit]");
  const submitIdle = form.querySelector("[data-admin-app-ticket-submit-idle]");
  const submitBusy = form.querySelector("[data-admin-app-ticket-submit-busy]");
  if (!submit || !submitIdle || !submitBusy) return;

  form.dataset.submissionBound = "true";
  const setSubmitting = (submitting) => {
    form.dataset.submitting = submitting ? "true" : "false";
    submit.disabled = submitting;
    submit.toggleAttribute("aria-busy", submitting);
    submitIdle.hidden = submitting;
    submitBusy.hidden = !submitting;
  };

  setSubmitting(false);
  form.addEventListener("submit", (event) => {
    if (form.dataset.ticketSubmissionResumed === "true") {
      delete form.dataset.ticketSubmissionResumed;
      return;
    }

    if (form.dataset.submitting === "true") {
      event.preventDefault();
      return;
    }

    // Give the native app a frame to show the locked progress state before
    // Turbo starts reading and uploading potentially large photos or videos.
    event.preventDefault();
    setSubmitting(true);
    window.requestAnimationFrame(() => {
      if (!form.isConnected) return;
      form.dataset.ticketSubmissionResumed = "true";
      form.requestSubmit();
    });
  });
  form.addEventListener("turbo:submit-end", (event) => {
    if (!event.detail?.success && form.isConnected) setSubmitting(false);
  });
}

document.addEventListener("DOMContentLoaded", () => setupAdminAppTicketCreateForm());
document.addEventListener("turbo:load", () => setupAdminAppTicketCreateForm());

function setupAdminAppDrawer(root = document) {
  const layer = root.querySelector("[data-admin-app-drawer]");
  if (!layer || layer.dataset.bound === "true") return;

  const close = layer.querySelector("[data-admin-app-drawer-close]");
  if (!close) return;
  layer.dataset.bound = "true";
  document.body.classList.add("admin-app-drawer-open");
  window.requestAnimationFrame(() => close.focus({ preventScroll: true }));

  layer.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    close.click();
  });
}

document.addEventListener("DOMContentLoaded", () => setupAdminAppDrawer());
document.addEventListener("turbo:load", () => setupAdminAppDrawer());
document.addEventListener("turbo:before-cache", () => document.body.classList.remove("admin-app-drawer-open"));

function initAdminMobileFrameContent(frame) {
  syncAdminMobileShell(frame);
  document.body.classList.remove("admin-app-drawer-open");
  initAdminMobileApp(frame);
  setupMailroomScanner(frame);
  setupMailroomDeliveryForm(frame);
  setupAdminAppTicketCreateForm(frame);
  setupAdminAppDrawer(frame);
  document.dispatchEvent(new CustomEvent("flextech:admin-mobile-content-load", { detail: { root: frame } }));
}

document.addEventListener("turbo:frame-load", (event) => {
  const frame = event.target;
  if (!(frame instanceof Element) || frame.id !== "admin-mobile-content") return;
  initAdminMobileFrameContent(frame);
});

// Promoted frame visits restore their cached frame on browser Back/Forward as
// a Turbo page render. Re-sync the permanent shell from that restored frame so
// its active navigation state follows history as well as fresh requests.
document.addEventListener("turbo:render", () => {
  const frame = adminMobileContentFrame();
  if (frame) initAdminMobileFrameContent(frame);
});

document.addEventListener("turbo:before-frame-render", (event) => {
  if (event.target instanceof Element && event.target.id === "admin-mobile-content") {
    document.body.classList.remove("admin-app-drawer-open");
  }
});

document.addEventListener("turbo:frame-missing", (event) => {
  if (!(event.target instanceof Element) || event.target.id !== "admin-mobile-content") return;
  event.preventDefault();
  window.location.assign(event.detail?.response?.url || window.location.href);
});
